import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Button, Checkbox, Table} from 'semantic-ui-react';
import {toast} from 'react-toastify';
import GridHeader from '../../components/GridHeader';
import {getMassCalculationDetails, queueMassCalculation, deleteMassCalculationRoutes} from '../../services/massUploadApi';
import TableStyled from '../../components/Table/Table';
import EditRouteAddressModal from './components/EditRouteAddressModal';

const EditableAddressCell = ({value, hasError, canEdit, onEdit}) => {
    const displayValue = value || 'пусто';

    if (!canEdit) {
        return (
            <span style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                color: hasError || !value ? '#dc3545' : 'inherit'
            }}>
                {displayValue}
            </span>
        );
    }

    return (
        <div
            onClick={onEdit}
            title="Нажмите для редактирования"
            style={{
                cursor: 'pointer',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                backgroundColor: hasError ? '#fff4f4' : 'transparent',
                color: hasError || !value ? '#dc3545' : 'inherit',
                padding: '2px 4px',
                borderRadius: '3px',
                minHeight: '24px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '8px'
            }}
        >
            <span>{displayValue}</span>
            <span className="glyphicon glyphicon-pencil" style={{fontSize: '11px', color: '#999', marginTop: '3px'}}/>
        </div>
    );
};

const MassUploadCalculationPage = ({match, history}) => {
    const fileId = parseInt(match.params.fileId, 10);
    const [fileDetails, setFileDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isCalculating, setIsCalculating] = useState(false);
    const [editModal, setEditModal] = useState({ open: false, route: null, fieldName: null, title: null });
    const mountedRef = useRef(true);
    const selectedRowsRef = useRef([]);

    useEffect(() => {
        mountedRef.current = true;
        loadDetails();
        return () => {
            mountedRef.current = false;
        };
    }, [fileId]);

    useEffect(() => {
        selectedRowsRef.current = selectedRows;
    }, [selectedRows]);

    const canModifyRows = !!(fileDetails && fileDetails.fileInfo && fileDetails.fileInfo.canDelete);
    const routes = useMemo(
        () => (fileDetails && fileDetails.routes) || [],
        [fileDetails]
    );

    const loadDetails = (showLoader = true, preserveSelection = false) => {
        if (showLoader) {
            setLoading(true);
        }

        const previousSelection = preserveSelection ? selectedRowsRef.current : [];

        getMassCalculationDetails(fileId)
            .then(data => {
                if (!mountedRef.current) return;
                setFileDetails(data);
                if (preserveSelection) {
                    const availableIds = new Set((data.routes || []).map(route => route.id));
                    setSelectedRows(previousSelection.filter(id => availableIds.has(id)));
                } else {
                    setSelectedRows([]);
                }
                if (showLoader) {
                    setLoading(false);
                }
            })
            .catch(err => {
                if (!mountedRef.current) return;
                console.error(err);
                const message = (err && (err.Error || err.error || err.message))
                    || 'Ошибка при загрузке маршрутов массового расчета';
                toast.error(message);
                if (showLoader) {
                    setLoading(false);
                }
            });
    };

    const handleRowSelect = (routeId, checked) => {
        if (!canModifyRows) {
            return;
        }
        if (checked) {
            setSelectedRows(prev => [...prev, routeId]);
        } else {
            setSelectedRows(prev => prev.filter(id => id !== routeId));
        }
    };

    const handleSelectAll = (checked) => {
        if (!canModifyRows) {
            return;
        }
        if (checked) {
            setSelectedRows(routes.map(route => route.id));
        } else {
            setSelectedRows([]);
        }
    };

    const openAddressEdit = (route, fieldName, title) => {
        if (!canModifyRows) {
            return;
        }
        setEditModal({ open: true, route, fieldName, title });
    };

    const closeAddressEdit = () => {
        setEditModal({ open: false, route: null, fieldName: null, title: null });
    };

    const handleAddressSaved = () => {
        loadDetails(false, true);
    };

    const handleCalculate = () => {
        const hasAddressErrors = routes.some(route => route.fromHasError || route.toHasError);

        if (routes.length === 0) {
            toast.warning('Список маршрутов пуст');
            return;
        }

        if (hasAddressErrors) {
            toast.warning('Исправьте ошибки адресов во всех маршрутах перед расчетом');
            return;
        }

        if (isCalculating) {
            return;
        }

        setIsCalculating(true);
        queueMassCalculation(fileId)
            .then(() => {
                toast.success('Маршруты поставлены в очередь на расчет');
                history.push('/massupload');
            })
            .catch(err => {
                console.error(err);
                let errorMessage = 'Ошибка при запуске расчета';
                if (err && typeof err === 'object') {
                    if (err.messages) {
                        const values = Object.values(err.messages).filter(Boolean);
                        if (values.length > 0) {
                            errorMessage = values.join('; ');
                        }
                    } else if (err.Error) {
                        errorMessage = err.Error;
                    } else if (err.error) {
                        errorMessage = err.error;
                    } else if (err.message) {
                        errorMessage = err.message;
                    }
                }
                toast.error(errorMessage);
                setIsCalculating(false);
            });
    };

    const handleDeleteSelected = () => {
        if (!canModifyRows) {
            toast.warning('Удаление маршрутов недоступно');
            return;
        }

        if (selectedRows.length === 0) {
            toast.warning('Выберите строки для удаления');
            return;
        }

        if (confirm(`Удалить ${selectedRows.length} выбранных строк?`)) {
            deleteMassCalculationRoutes(fileId, selectedRows)
                .then(() => {
                    toast.success('Строки удалены');
                    setSelectedRows([]);
                    loadDetails();
                })
                .catch(err => {
                    console.error(err);
                    const message = (err && (err.Error || err.error || err.message))
                        || 'Ошибка при удалении строк';
                    toast.error(message);
                });
        }
    };

    const truncateFileName = (fileName, maxLength = 50) => {
        if (!fileName || fileName.length <= maxLength) {
            return fileName;
        }
        return fileName.substring(0, maxLength) + '...';
    };

    if (loading) {
        return (
            <div className="content-wrapper">
                <section className="content">
                    <div className="box">
                        <div className="box-body">
                            <div className="text-center" style={{padding: '50px'}}>
                                <i className="fa fa-spinner fa-spin fa-3x text-primary"></i>
                                <p style={{marginTop: '20px'}}>Загрузка данных...</p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    if (!fileDetails || !fileDetails.fileInfo) {
        return (
            <div className="content-wrapper">
                <section className="content">
                    <div className="box box-danger">
                        <div className="box-body">
                            <div className="alert alert-danger">
                                <i className="fa fa-exclamation-triangle"></i> Файл не найден
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    const errorRoutesCount = routes.filter(route => route.fromHasError || route.toHasError).length;
    const allSelected = canModifyRows
        && routes.length > 0
        && selectedRows.length === routes.length;
    const canCalculate = routes.length > 0 && errorRoutesCount === 0 && !isCalculating;

    return (
        <div className='new-styles new-styles__p-0' style={{overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100vh'}}>
            <div className='grid__header'>
                <GridHeader
                    name={`Массовый расчет / ${truncateFileName(fileDetails.fileInfo.originalFileName)}`}
                    title={`Массовый расчет / ${fileDetails.fileInfo.originalFileName}`}
                    btn={
                        <div style={{display: 'flex', gap: '8px'}}>
                            {canModifyRows && (
                                <Button
                                    size="small"
                                    onClick={handleDeleteSelected}
                                    disabled={selectedRows.length === 0}
                                    title="Удалить выбранные строки"
                                >
                                    <i className="fa fa-trash"></i> Удалить выбранные
                                </Button>
                            )}
                            <Button
                                className='btn_light-blue'
                                onClick={handleCalculate}
                                disabled={!canCalculate}
                                loading={isCalculating}
                                title={errorRoutesCount > 0
                                    ? 'Исправьте ошибки адресов во всех маршрутах'
                                    : routes.length === 0
                                        ? 'Список маршрутов пуст'
                                        : 'Рассчитать все маршруты'}
                            >
                                Рассчитать
                            </Button>
                        </div>
                    }
                />
            </div>

            <div style={{padding: '15px 20px', backgroundColor: '#fff', borderBottom: '1px solid #ddd'}}>
                {errorRoutesCount > 0 ? (
                    <span className="text-warning" style={{fontSize: '14px'}}>
                        <i className="fa fa-exclamation-triangle"></i>
                        {' '}Маршрутов с ошибками адресов <strong className="text-danger">{errorRoutesCount} шт.</strong>
                    </span>
                ) : (
                    <span className="text-success" style={{fontSize: '14px'}}>
                        <i className="fa fa-check-circle"></i>
                        {' '}Все маршруты без ошибок адресов
                    </span>
                )}
            </div>

            <div style={{
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0
            }}>
                <div style={{
                    overflowX: 'auto',
                    overflowY: 'auto',
                    backgroundColor: '#fff',
                    flex: 1,
                    minHeight: 0,
                    padding: '0 20px 20px'
                }}>
                    <TableStyled striped={true}>
                        <Table.Header style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 101,
                            backgroundColor: '#fff'
                        }}>
                            <Table.Row>
                                {canModifyRows && (
                                    <Table.HeaderCell className='table-grid__checkbox' style={{backgroundColor: '#fff'}}>
                                        <Checkbox
                                            checked={allSelected}
                                            disabled={routes.length === 0}
                                            onChange={(e, {checked}) => handleSelectAll(checked)}
                                        />
                                    </Table.HeaderCell>
                                )}
                                <Table.HeaderCell style={{minWidth: '70px', textAlign: 'center', backgroundColor: '#fff'}}>
                                    Ошибки
                                </Table.HeaderCell>
                                <Table.HeaderCell style={{minWidth: '70px', backgroundColor: '#fff'}}>
                                    № ПП
                                </Table.HeaderCell>
                                <Table.HeaderCell style={{minWidth: '250px', backgroundColor: '#fff'}}>
                                    Адрес отправления
                                </Table.HeaderCell>
                                <Table.HeaderCell style={{minWidth: '250px', backgroundColor: '#fff'}}>
                                    Вы указали в шаблоне
                                </Table.HeaderCell>
                                <Table.HeaderCell style={{minWidth: '250px', backgroundColor: '#fff'}}>
                                    Адрес доставки
                                </Table.HeaderCell>
                                <Table.HeaderCell style={{minWidth: '250px', backgroundColor: '#fff'}}>
                                    Вы указали в шаблоне
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {routes.map(route => {
                                const hasErrors = route.fromHasError || route.toHasError;
                                const errorCount = (route.fromHasError ? 1 : 0) + (route.toHasError ? 1 : 0);
                                const isSelected = selectedRows.includes(route.id);

                                return (
                                    <Table.Row
                                        key={route.id}
                                        className={hasErrors ? 'table-grid__row table-grid__row_red' : 'table-grid__row'}
                                    >
                                        {canModifyRows && (
                                            <Table.Cell className='table-grid__checkbox_row'>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={(e, {checked}) => handleRowSelect(route.id, checked)}
                                                />
                                            </Table.Cell>
                                        )}
                                        <Table.Cell style={{textAlign: 'center'}}>
                                            {hasErrors ? (
                                                <span
                                                    className="error-badge"
                                                    title={route.errors}
                                                    style={{
                                                        backgroundColor: '#dc3545',
                                                        color: '#fff',
                                                        padding: '2px 6px',
                                                        borderRadius: '10px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {errorCount}
                                                </span>
                                            ) : (
                                                <span style={{color: '#28a745', fontWeight: 'bold'}}>✓</span>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>{route.rowNumber}</Table.Cell>
                                        <Table.Cell style={{minWidth: '250px'}}>
                                            <EditableAddressCell
                                                value={route.fromAddress}
                                                hasError={route.fromHasError}
                                                canEdit={canModifyRows}
                                                onEdit={() => openAddressEdit(route, 'fromAddress', 'Адрес отправления')}
                                            />
                                        </Table.Cell>
                                        <Table.Cell style={{whiteSpace: 'normal', wordBreak: 'break-word', backgroundColor: '#fff8e6'}}>
                                            {route.templateFromAddress || '—'}
                                        </Table.Cell>
                                        <Table.Cell style={{minWidth: '250px'}}>
                                            <EditableAddressCell
                                                value={route.toAddress}
                                                hasError={route.toHasError}
                                                canEdit={canModifyRows}
                                                onEdit={() => openAddressEdit(route, 'toAddress', 'Адрес доставки')}
                                            />
                                        </Table.Cell>
                                        <Table.Cell style={{whiteSpace: 'normal', wordBreak: 'break-word', backgroundColor: '#fff8e6'}}>
                                            {route.templateToAddress || '—'}
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </TableStyled>
                    {routes.length === 0 && (
                        <div style={{padding: '30px', textAlign: 'center', color: '#777'}}>
                            Маршруты не найдены
                        </div>
                    )}
                </div>
            </div>

            <EditRouteAddressModal
                open={editModal.open}
                route={editModal.route}
                allRoutes={routes}
                fileId={fileId}
                fieldName={editModal.fieldName}
                title={editModal.title}
                onClose={closeAddressEdit}
                onSave={handleAddressSaved}
            />
        </div>
    );
};

export default MassUploadCalculationPage;



// WEBPACK FOOTER //
// ./App/pages/massUpload/massUploadCalculationPage.jsx